import { routesPodcastsKeywords } from "@app/routePaths";
import { Tag, TagField } from "@app/types/Cue";
import {
  defaultKeywordDataFetcher,
  fetchWithHighlightArticlesKeywordDataFetcher,
  fetchWithHighlightKeywordDataFetcher,
} from "@pages/Keyword/KeywordPage.server";
import { CampaignTemplateKeywordContext } from "@pages/Keyword/templates/CampaignTemplate/types";
import { KeywordContext } from "@typings/Page";
import { textToSlug } from "@util/helpers";

import {
  FeedTagData,
  FeedTagEntity,
  FeedVideosType,
  FieldKey,
  VideoItemsType,
} from "./types";

/**
 * Helper function to get the page size for the keyword page
 * @param path The path of the keyword page
 * @returns {number} The page size
 */
export const getKeywordPageSize = (path: string): number => {
  if (routesPodcastsKeywords.includes(path)) return 5;

  return 10;
};

/**
 * Helper function to determine which data fetcher to use based on the tag path
 * @param tag The tag object containing path information
 * @returns The appropriate data fetcher function for the given path
 */
export const whichKeywordDataFetcher = (tag: FeedTagData) => {
  const template = tag.entity.template;

  if (typeof template === "string") {
    return getKeywordDataFetcherByTemplate(template);
  }

  return defaultKeywordDataFetcher;
};

/**
 * Returns the appropriate keyword data fetcher function based on the template type
 * @param {string} template - Template identifier (e.g. "withHighlightArticles")
 * @returns defaultKeywordDataFetcher for unknown templates, fetchWithHighlightKeywordDataFetcher for "withHighlightArticles"
 */
export const getKeywordDataFetcherByTemplate = (template: string) => {
  switch (template) {
    case "withHighlightArticles":
      return fetchWithHighlightKeywordDataFetcher;

    case "campaignTemplate1Budget":
      return fetchWithHighlightArticlesKeywordDataFetcher;

    default:
      return defaultKeywordDataFetcher;
  }
};

/**
 * Extracts and returns an array of story queue IDs from a tag's fields
 * @param {Tag} tag - The tag object containing story queue information in its fields
 * @returns {string[]} An array of story queue IDs, or empty array if no valid story queues found
 */
export const getStoryQueueIdsFromKeyword = (tag: Tag): string[] => {
  const storyQueues = getFieldValueByKey(tag.fields, FieldKey.StoryQueues);

  if (typeof storyQueues === "undefined") return [];

  return storyQueues.split(",");
};

/**
 * Helper function to get the value from a key-value pair array by key.
 * @param fields - Array of key-value pairs to search through
 * @param key - The key to look up
 * @returns {string|undefined} The value associated with the key if found, undefined otherwise
 */
export const getFieldValueByKey = (
  fields: TagField[],
  key: string
): string | undefined => {
  const field = fields.find((field) => field.key === key);
  const caasValue = field?.["value-caas"];

  if (caasValue) return caasValue;

  return field?.value;
};

/**
 * Finds the appropriate URL path for a 301 redirection of a given keyword URL path.
 *
 * @param {string} keywordUrlPath - The keyword URL path that needs redirection.
 * @param {Tag[]} tags - An array of tags to check against.
 * @returns {string | null} - Returns the matching `urlPath` if a redirection is needed, otherwise `null`.
 */
export const getRedirectUrlPathForTag = (
  tagUrlPath: string,
  tagEntity: FeedTagEntity
): string | null => {
  const tag = tagEntity.tag;

  if (
    tagEntity.urlPathHistory.includes(tagUrlPath) &&
    tag.urlPath !== tagUrlPath
  ) {
    return tag.urlPath;
  }

  return null;
};

export const getUrlToBeInTheCorrectOrder = (
  tagEntity: FeedTagEntity
): string[] => {
  const urlPathHistory = tagEntity.urlPathHistory;
  const currentUrlPath = tagEntity.tag.urlPath;

  // Remove currentUrlPath from urlPathHistory if it exists
  const filteredHistory = urlPathHistory.filter(
    (path) => path !== currentUrlPath
  );

  // Return a new array with currentUrlPath at the front
  return [currentUrlPath, ...filteredHistory];
};

export const isCampaignTemplateBudgetData = (
  data: KeywordContext["data"] | CampaignTemplateKeywordContext["data"]
): data is CampaignTemplateKeywordContext["data"] => {
  return data.template === "campaignTemplate1Budget";
};

/**
 * Helper function to get the ref code for the keyword page with campaign template 1 budget
 * @param urlPath - The URL path of the keyword page
 * @param blockName - The name of the block
 * @returns The ref code for the keyword page with campaign template 1 budget
 */
export const getRefCode = ({
  urlPath,
  blockName,
}: {
  urlPath: string;
  blockName: string;
}): string => {
  const budgetPattern = "/keywords/singapore-budget-";

  if (urlPath.startsWith(budgetPattern)) {
    const budgetYear = urlPath.replaceAll(budgetPattern, "");

    return `budget${budgetYear}-${blockName}`;
  }

  return blockName;
};

/**
 * Helper function to append the URL path to the feed videos type
 * @param video - The feed videos type to append the URL path to
 * @param refCode - The reference code to append to the URL path
 * @returns The feed videos type with the URL path appended
 */
export const appendUrlPathToFeedVideosType = (
  video: FeedVideosType,
  refCode: string
): FeedVideosType => {
  return {
    ...video,
    items: video.items.map((item): VideoItemsType => {
      return {
        ...item,
        videoData: {
          ...item.videoData,
          urlPath: `/videos/${textToSlug(item.videoData.name)}/${item.videoData.id}?ref=${refCode}`,
        },
      };
    }),
  };
};
